import React, { useState, useMemo } from "react";
import CollapsibleDataCard from "@/components/play/CollapsibleDataCard";
import { useCockpitPreferences } from "@/hooks/play/useCockpitPreferences";
import { useGetUserLevel } from "@/hooks/useGetUserLevel";
import { useGetPlayStats } from "@/hooks/play/useGetPlayStats";
import { FiTrendingUp } from "react-icons/fi";
import { RiVipDiamondLine } from "react-icons/ri";
import { FaRegClock } from "react-icons/fa";
import { IoGameControllerOutline } from "react-icons/io5";
import { Token } from "@devour/client";
import "./StatsCard.scss";

interface StatsCardProps {
    fullToken?: Token;
    userId?: string;
}

type TimeFilter = "day" | "week" | "month" | "year" | "all";

const StatsCard: React.FC<StatsCardProps> = ({ fullToken, userId }) => {
    const { selectedTimeRange, setTimeRange } = useCockpitPreferences();
    const [timeFilter, setTimeFilter] = useState<TimeFilter>((selectedTimeRange as TimeFilter) || "day");

    // Fetch user level data (includes XP transactions)
    const { data: userLevelData, isLoading: levelLoading } = useGetUserLevel(
        fullToken,
        userId,
        !fullToken || !userId,
    );


    // Fetch play stats with time filter
    const { data: playStats, isLoading: playStatsLoading } = useGetPlayStats(
        fullToken?.token ? `Bearer ${fullToken.token}` : null,
        timeFilter,
    );

    // Handle time filter change
    const handleTimeFilterChange = (filter: TimeFilter) => {
        setTimeFilter(filter);
        setTimeRange(filter);
    };


    // Use XP stats from enhanced play-stats endpoint
    const xpStats = useMemo(() => {
    // Use enhanced play-stats endpoint data with EST timezone and proper DEBIT/CREDIT handling
        if (playStats) {
            return {
                xpOverall: playStats.xpOverall || 0,
                xpGaming: playStats.xpGaming || 0,
            };
        }

        // No data available yet
        return { xpOverall: 0, xpGaming: 0 };
    }, [playStats]);

    // Get time and games stats - use only the enhanced play-stats endpoint
    const gameStats = useMemo(() => {
        if (playStats) {
            return {
                timePlayed: playStats.timePlayed || 0,
                gamesPlayed: playStats.gamesPlayed || 0,
            };
        }

        return {
            timePlayed: 0,
            gamesPlayed: 0,
        };
    }, [playStats]);

    // Format time display - always show in hours
    const formatTime = (minutes: number): string => {
        if (minutes === 0) return "0h";
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;

        // For large hour counts, just show hours
        if (hours >= 100) {
            return `${hours.toLocaleString()}h`;
        }

        // For smaller amounts, show hours and minutes
        if (mins > 0 && hours < 100) {
            return `${hours}h ${mins}m`;
        }

        return `${hours}h`;
    };

    // Don't show loading state to prevent card resizing during filter changes
    const isInitialLoading = (levelLoading || playStatsLoading) && !userLevelData && !playStats;

    return (
        <CollapsibleDataCard
            id="stats-card"
            title="My Stats"
            icon={<FiTrendingUp />}
            defaultExpanded={true}
            isLoading={isInitialLoading}
            hasData={true}
        >
            <div className="stats-card">
                <div className="stats-card__filters">
                    {(["day", "week", "month", "year", "all"] as TimeFilter[]).map(filter =>
                        <button
                            key={filter}
                            className={`stats-card__filter ${timeFilter === filter ? "active" : ""}`}
                            onClick={() => handleTimeFilterChange(filter)}
                        >
                            {filter === "all" ? "All" : filter.charAt(0).toUpperCase()}
                        </button>)}
                </div>

                <div className="stats-card__content">
                    <div className="stats-card__item">
                        <RiVipDiamondLine className="stats-card__icon" />
                        <span className="stats-card__label">Overall XP</span>
                        <span className="stats-card__value">
                            {playStatsLoading && !playStats ? "..." : xpStats.xpOverall.toLocaleString()}
                        </span>
                    </div>

                    <div className="stats-card__item">
                        <IoGameControllerOutline className="stats-card__icon" />
                        <span className="stats-card__label">Gaming XP</span>
                        <span className="stats-card__value">
                            {playStatsLoading && !playStats ? "..." : xpStats.xpGaming.toLocaleString()}
                        </span>
                    </div>

                    <div className="stats-card__item">
                        <FaRegClock className="stats-card__icon" />
                        <span className="stats-card__label">Time Played</span>
                        <span className="stats-card__value">
                            {playStatsLoading ? "..." : formatTime(gameStats.timePlayed)}
                        </span>
                    </div>

                    <div className="stats-card__item">
                        <IoGameControllerOutline className="stats-card__icon" />
                        <span className="stats-card__label">Games Played</span>
                        <span className="stats-card__value">
                            {playStatsLoading ? "..." : gameStats.gamesPlayed}
                        </span>
                    </div>
                </div>
            </div>
        </CollapsibleDataCard>
    );
};

export default React.memo(StatsCard);